import {
  isCompletedRequest,
} from './kioskTransforms';

function getBookingPacksForLoad(requestLoad) {
  const bookedItems = Array.isArray(requestLoad?.bookedItems) ? requestLoad.bookedItems : [];
  if (bookedItems.length === 0) {
    return null;
  }

  return bookedItems.reduce((total, item) => {
    const quantity = Number(item?.quantityPacks);
    return total + (Number.isFinite(quantity) ? Math.max(quantity, 0) : 0);
  }, 0);
}

function calculateScannedPacks(requests, loadDirection) {
  return requests
    .filter(isCompletedRequest)
    .reduce((total, request) => {
      const quantity = Number(request?.palletQuantity);
      const scannedPacks = Number.isFinite(quantity) ? Math.max(quantity, 0) : 0;
      const directionMultiplier = loadDirection
        && request?.requestDirection
        && loadDirection !== request.requestDirection
        ? -1
        : 1;
      return total + (scannedPacks * directionMultiplier);
    }, 0);
}

function getPackProgress(scannedPacks, bookedPacks, hasLinkedBooking) {
  const actual = Math.max(Number(scannedPacks) || 0, 0);
  const target = Number(bookedPacks) || 0;

  if (target <= 0) {
    return hasLinkedBooking
      ? {
        barPercent: 0,
        displayPercent: 0,
        hasTarget: false,
        isOver: false,
      }
      : null;
  }

  const rawPercent = (actual / target) * 100;

  return {
    barPercent: Math.min(Math.max(rawPercent, 0), 100),
    displayPercent: Math.round(rawPercent),
    hasTarget: true,
    isOver: actual > target,
  };
}

function isProductPackComplete(product) {
  const bookedPacks = Math.max(Number(product?.bookedPacks) || 0, 0);
  const scannedPacks = Math.max(Number(product?.scannedPacks) || 0, 0);
  return scannedPacks === bookedPacks;
}

function shortenPalletTypeName(value) {
  const palletTypeName = String(value ?? '').trim();
  const shortenedName = palletTypeName.replace(/^พาเลท\s*/u, '').trim();
  return shortenedName || palletTypeName;
}

export function KioskLoadCards({
  requestLoads,
  requests,
  selectedLoadId = '',
  selectedLoadDirection = '',
  canSelectLoad = false,
  onSelectLoad,
  onOpenDetail,
}) {
  if (requestLoads.length === 0) {
    return (
      <div className="kiosk-empty">
        <strong>ไม่มีโหลดที่เปิดอยู่</strong>
        <p>เปิดโหลดไม่จากหน้ารายการจอง </p>
      </div>
    );
  }

  return (
    <div className="kiosk-load-card-grid">
      {requestLoads.map((requestLoad) => {
        const matchedRequests = requests.filter((request) => request.requestLoadId === requestLoad.loadId);
        const relatedRequestsById = new Map(
          (requestLoad.weighingRequests ?? [])
            .filter((request) => Boolean(request?.requestId))
            .map((request) => [request.requestId, request]),
        );

        for (const request of matchedRequests) {
          if (!request?.requestId) {
            continue;
          }

          const existing = relatedRequestsById.get(request.requestId);
          if (!existing) {
            relatedRequestsById.set(request.requestId, request);
            continue;
          }

          // Keep richer fields from either source so totals still work when one payload is partial.
          relatedRequestsById.set(request.requestId, {
            ...existing,
            ...request,
            requestStatus: request.requestStatus ?? existing.requestStatus,
            requestDirection: request.requestDirection ?? existing.requestDirection,
            palletQuantity: request.palletQuantity ?? existing.palletQuantity,
            numericScaleValue: request.numericScaleValue ?? existing.numericScaleValue,
            palletWeight: request.palletWeight ?? existing.palletWeight,
            scaleValue: request.scaleValue != null && request.scaleValue !== ''
              ? request.scaleValue
              : existing.scaleValue,
          });
        }

        const relatedRequests = [...relatedRequestsById.values()];
        const aggregate = requestLoad.aggregate;
        const hasAggregate = aggregate && typeof aggregate === 'object';
        const scannedPacks = hasAggregate
          ? Math.max(Number(aggregate.scannedPacks) || 0, 0)
          : Math.max(calculateScannedPacks(relatedRequests, requestLoad.direction), 0);
        const hasLinkedBooking = Boolean(requestLoad.tpnBookingId);
        const bookedPacks = hasAggregate
          ? Math.max(Number(aggregate.bookedPacks) || 0, 0)
          : getBookingPacksForLoad(requestLoad);
        const products = hasAggregate && Array.isArray(aggregate.products)
          ? aggregate.products
          : [];
        const progress = getPackProgress(scannedPacks, bookedPacks, hasLinkedBooking);
        const isSelected = selectedLoadId === requestLoad.loadId;

        // Keyboard accessibility handler
        const handleKeyDown = (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectLoad(requestLoad);
          }
        };

        return (

          <article
            key={requestLoad.loadId}
            className={[
              'kiosk-load-card',
              progress?.isOver ? 'kiosk-load-card-over' : '',
              isSelected ? 'kiosk-load-card-selected' : '',
              isSelected && (selectedLoadDirection || requestLoad.direction) === 'Inbound'
                ? 'kiosk-load-card-selected-inbound'
                : '',
            ].filter(Boolean).join(' ')}
            style={progress ? { '--kiosk-load-progress': `${progress.barPercent}%` } : undefined}
          >
            <div onClick={() => onSelectLoad(requestLoad)}
              onKeyDown={handleKeyDown}
              tabIndex={0} 
              role="checkbox" 
              aria-checked={isSelected} 
              style={{
              // backgroundColor: isSelected ? '#e6f2ff' : '#ffffff',
                backgroundColor: 'transparent',
                color: '#333333',
                cursor: 'pointer',
                borderRadius: '8px',
                outline: 'none',
                textAlign: 'center',
                fontFamily: 'sans-serif',
                userSelect: 'none',
                fontSize: '18px',
                fontWeight: 'bold',
                transition: 'all 0.2s ease',
                boxShadow: isSelected ? '0 4px 12px rgba(0,123,255,0.2)' : 'none'
              }}>
              <div className="kiosk-request-top">
                <div>
                  <h3>{requestLoad.name}</h3>
                </div>

              </div>
              {progress ? (
                <div className="kiosk-load-progress" aria-label={`Load pack progress ${progress.displayPercent}%`}>
                  <div className="kiosk-load-progress-top">
                    <span>จำนวนสแกน/จำนวนจอง</span>
                    <strong>{progress.displayPercent}%</strong>
                  </div>
                  <div className="kiosk-load-progress-track" aria-hidden="true">
                    <span />
                  </div>
                  <p>
                    {progress.hasTarget
                      ? `${scannedPacks} / ${bookedPacks} packs`
                      : `${scannedPacks} / ไม่มีจำนวนจอง`}
                  </p>
                  {products.length > 0 ? (
                    <div className="kiosk-load-products">
                      <div className="kiosk-load-product-list-label">จำนวนที่จะต้องโหลด</div>
                      <ul className="kiosk-load-product-list" aria-label="Load products">
                        {products.map((product, index) => {
                        const isComplete = isProductPackComplete(product);
                        const productName = String(
                          product?.canonicalProductName ?? product?.productName ?? '',
                        ).trim() || 'Unknown product';
                        const palletTypeName = shortenPalletTypeName(product?.palletTypeName);
                        const productLabel = palletTypeName
                          ? `${productName} (${palletTypeName})`
                          : productName;
                        const fullPalletCount = Number(product?.fullPalletCount);
                        const remainingPacks = Number(product?.remainingPacks);
                        const hasPalletBreakdown = Number.isInteger(fullPalletCount)
                          && Number.isInteger(remainingPacks);
                        const hasNegativePalletBreakdown = hasPalletBreakdown
                          && (fullPalletCount < 0 || remainingPacks < 0);
                        const palletBreakdown = hasPalletBreakdown
                          ? `เต็ม/เศษ:${fullPalletCount}/${remainingPacks}`
                          : '';
                        const productKey = [
                          productName,
                          palletTypeName,
                       //   product?.quantityPacks,
                     //     product?.bookedPacks,
                          product?.canonicalProductId,
                          product?.palletTypeId,
                          index,
                        ].filter(Boolean).join('-');
                        return (
                          <li
                            key={productKey}
                            className={[
                              isComplete ? 'complete' : 'incomplete',
                              hasNegativePalletBreakdown ? 'negative' : '',
                            ].filter(Boolean).join(' ')}
                            aria-label={`${productLabel}: ${isComplete ? 'complete' : 'incomplete'}`}
                          >
                            <span className="kiosk-load-product-indicator" aria-hidden="true" />
                            <span>{productLabel}</span>
                            {hasPalletBreakdown ? (
                              <span className="kiosk-load-product-packs">
                                {palletBreakdown}
                              </span>
                            ) : null}
                          </li>
                        );
                        })}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null} 
            </div>
            <div className="kiosk-actions kiosk-load-card-actions" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
              {onSelectLoad ? (
                <button
                  type="button"
                  className={isSelected ? 'kiosk-primary-btn kiosk-load-select-btn' : 'kiosk-secondary-btn kiosk-load-select-btn'}
                  disabled={!canSelectLoad || isSelected}
                  onClick={() => onSelectLoad(requestLoad)}
                >
                  เลือก
                </button>
              ) : null}
              <button
                type="button"
                className="kiosk-secondary-btn kiosk-load-detail-btn"
                onClick={() => onOpenDetail(requestLoad.loadId)}
              >
                รายละเอียด
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
